import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import UserInteractionsController from './userInteractions.controller';
import { Layout, TableSkeleton, PageHeader, StatCard, SectionCard, StatusTag, EmptyState, FilterBar, FilterField } from '../common';
import { Button, DatePicker, Table, Alert, Progress } from 'antd';
import { MousePointerClick, Users, UserCheck, UserX, CircleCheck, CircleX, Activity, TriangleAlert, RotateCw, ListChecks } from 'lucide-react';
import { semantic } from '../../theme/tokens';
import { formatNumber } from '../../utils/format';
import dayjs from 'dayjs';

const USER_TYPE_LABELS = {
    promotor: 'Promotor',
    asesor: 'Asesor',
    auditor: 'Auditor'
};

const rateTone = (percent) => (percent >= 80 ? 'success' : percent >= 50 ? 'warning' : 'danger');

class UserInteractions extends Inherit(UserInteractionsController, Component) {
    state = {
        loading: true,
        summary: [],
        inactiveUsers: [],
        totalUsers: 0,
        date: dayjs().format('YYYY-MM-DD'),
        error: false
    };

    render() {
        const { loading, summary, inactiveUsers, totalUsers, date, error } = this.state;
        const { activeCount, activePercent } = this.getActivityStats();
        const activityTone = rateTone(activePercent);

        const columns = [
            {
                title: 'Usuario',
                dataIndex: ['user', 'name'],
                key: 'name',
                render: (v) => <span className="text-sm font-medium text-surface-900">{v || '—'}</span>
            },
            { title: 'Email', dataIndex: ['user', 'email'], key: 'email', render: (v) => <span className="text-sm text-surface-600">{v || '—'}</span> },
            {
                title: 'Tipo',
                dataIndex: ['user', 'userType'],
                key: 'userType',
                render: (v) => <StatusTag>{USER_TYPE_LABELS[v] || v}</StatusTag>
            },
            {
                title: 'Interacciones',
                dataIndex: 'interactionCount',
                key: 'interactionCount',
                align: 'right',
                sorter: (a, b) => a.interactionCount - b.interactionCount,
                render: (count) => (
                    <StatusTag tone={count > 0 ? 'success' : 'danger'} className="tabular-nums">
                        {count}
                    </StatusTag>
                )
            },
            {
                title: 'Estado',
                dataIndex: 'flagged',
                key: 'flagged',
                filters: [
                    { text: 'Activo', value: false },
                    { text: 'Sin actividad', value: true }
                ],
                onFilter: (value, record) => record.flagged === value,
                render: (flagged) =>
                    flagged ? (
                        <StatusTag tone="danger" icon={<CircleX />}>
                            Sin actividad
                        </StatusTag>
                    ) : (
                        <StatusTag tone="success" icon={<CircleCheck />}>
                            Activo
                        </StatusTag>
                    )
            }
        ];

        return (
            <Layout current="user-interactions">
                <PageHeader
                    icon={<MousePointerClick />}
                    title="Interacciones de Usuarios"
                    description="Auditoría de clics e interacciones por usuario en la app móvil."
                />

                <FilterBar>
                    <FilterField label="Fecha" width={200}>
                        <DatePicker
                            className="w-full"
                            value={dayjs(date)}
                            onChange={(d) => d && this.loadSummary(d)}
                            format="DD/MM/YYYY"
                            allowClear={false}
                        />
                    </FilterField>
                </FilterBar>

                {error ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<TriangleAlert />}
                            title="Error al cargar datos de interacciones"
                            description="Revisa tu conexión e inténtalo de nuevo."
                            action={
                                <Button icon={<RotateCw />} onClick={() => this.retry()}>
                                    Reintentar
                                </Button>
                            }
                        />
                    </div>
                ) : loading ? (
                    <TableSkeleton rows={10} columns={5} />
                ) : (
                    <div className="flex flex-col gap-4">
                        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                            <StatCard label="Total usuarios" value={formatNumber(totalUsers)} icon={<Users />} tone="brand" />
                            <StatCard label="Usuarios activos" value={formatNumber(activeCount)} valueTone="success" icon={<UserCheck />} tone="success" />
                            <StatCard
                                label="Sin actividad"
                                value={formatNumber(inactiveUsers.length)}
                                valueTone={inactiveUsers.length > 0 ? 'danger' : undefined}
                                icon={<UserX />}
                                tone="danger"
                            />
                            <StatCard
                                label="Tasa de actividad"
                                value={`${activePercent}%`}
                                valueTone={activityTone}
                                icon={<Activity />}
                                tone={activityTone}
                                footer={
                                    <Progress
                                        percent={activePercent}
                                        strokeColor={semantic[activityTone].solid}
                                        showInfo={false}
                                        size={['100%', 6]}
                                        className="!m-0"
                                    />
                                }
                            />
                        </div>

                        {inactiveUsers.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                icon={<TriangleAlert />}
                                message={`${inactiveUsers.length} usuario(s) sin interacciones el ${dayjs(date).format('DD/MM/YYYY')}`}
                            />
                        )}

                        <SectionCard
                            title="Detalle por usuario"
                            description={dayjs(date).format('DD/MM/YYYY')}
                            icon={<ListChecks />}
                            flush
                            bodyClassName="[&_.ant-pagination]:px-5">
                            {summary.length === 0 ? (
                                <EmptyState icon={<MousePointerClick />} title="Sin datos para esta fecha" />
                            ) : (
                                <Table
                                    dataSource={summary}
                                    columns={columns}
                                    size="middle"
                                    scroll={{ x: 'max-content' }}
                                    rowKey={(r) => r.user?._id}
                                    pagination={{ pageSize: 20, showSizeChanger: true }}
                                />
                            )}
                        </SectionCard>
                    </div>
                )}
            </Layout>
        );
    }
}

export default withNavigate(UserInteractions);
